import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

/**
 * Every world, and whether players can get into it.
 *
 * A SEPARATE COMPONENT FROM THE PAGE from the very first commit, deliberately.
 * `AdminNpcs.js` is 442 lines in one function — the largest single-responsibility
 * file in the codebase — and the world editor is going to be bigger than that
 * page. Splitting after the fact never happens.
 *
 * THE STATE THAT MATTERS HERE is not "on or off" but "why can't I turn this
 * off". Three different situations produce a disabled toggle and they need
 * different words: it is the only world, it came from a file and has no
 * document, or it will not parse.
 */

const badge = (text, tone) => (
  <span className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{text}</span>
);

function WorldList({
  worlds, fileOnly, busy, onToggle, onImport,
}) {
  return (
    <div className="space-y-6">
      <ul className="space-y-2">
        {/*
          NEVER "there are no worlds" WHEN THERE ARE. This said exactly that
          while three shipped worlds were running and listed further down the
          same page — because it meant "no EDITABLE worlds" and did not say so.
          The two cases are genuinely different and now read differently.
        */}
        {worlds.length === 0 && fileOnly.length > 0 && (
          <li className="rounded border border-dashed border-gray-300 p-6 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {`Your ${fileOnly.length} world(s) are running`}
            </span>
            {' — they just cannot be edited yet, because they shipped as files. '}
            Import one below and you can draw on it.
          </li>
        )}

        {worlds.length === 0 && fileOnly.length === 0 && (
          <li className="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700">
            No worlds at all yet. Make one with
            {' '}
            <span className="font-medium">New world</span>
            .
          </li>
        )}

        {worlds.map((world) => (
          <li
            key={world.name}
            className="rounded border border-gray-200 p-3 dark:border-gray-800"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {world.title || world.name}
              </span>
              <code className="text-xs text-gray-500">{world.name}</code>

              {world.enabled
                ? badge('on', 'bg-emerald-500/15 text-emerald-500')
                : badge('off', 'bg-gray-500/15 text-gray-500')}

              {world.problem && badge('will not load', 'bg-red-500/15 text-red-500')}
              {world.players > 0 && badge(`${world.players} inside`, 'bg-blue-500/15 text-blue-500')}

              {world.hasDraft && badge('draft', 'bg-amber-500/15 text-amber-500')}

              <Link
                to={`/admin/worlds/${world.name}`}
                className="ml-auto rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-500"
              >
                Edit
              </Link>

              <button
                type="button"
                disabled={busy === world.name || Boolean(world.problem)}
                onClick={() => onToggle(world)}
                className={`rounded px-3 py-1 text-sm disabled:opacity-40 ${
                  world.enabled
                    ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                    : 'bg-emerald-600 text-white hover:bg-emerald-500'
                }`}
              >
                {/* eslint-disable-next-line no-nested-ternary */}
                {busy === world.name ? '…' : (world.enabled ? 'Turn off' : 'Turn on')}
              </button>
            </div>

            <p className="mt-1 text-xs text-gray-500">
              {world.widthTiles}
              ×
              {world.heightTiles}
              {' · '}
              {world.spawns}
              {' spawn(s) · '}
              {world.portals}
              {' portal(s) · '}
              {world.zones}
              {' zone(s)'}
            </p>

            {/*
              A world that will not parse is listed with the reason rather than
              hidden — it is already missing from the picker, and vanishing from
              the admin page too would leave nowhere to find out why.
            */}
            {world.problem && (
              <p className="mt-2 rounded bg-red-500/10 px-2 py-1 text-xs text-red-500">
                {world.problem}
              </p>
            )}

            {(world.warnings || []).map((w) => (
              <p key={w} className="mt-1 text-xs text-amber-500">{w}</p>
            ))}
          </li>
        ))}
      </ul>

      {fileOnly.length > 0 && (
        <div className="rounded border border-gray-200 p-3 text-sm dark:border-gray-800">
          <div className="flex items-center gap-2">
            <p className="font-medium text-gray-900 dark:text-gray-100">Shipped as files</p>
            <button
              type="button"
              disabled={busy === '*'}
              onClick={() => onImport(null)}
              className="ml-auto rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-500 disabled:opacity-40"
            >
              {busy === '*' ? 'Importing…' : 'Import all'}
            </button>
          </div>

          {/*
            Says what these ARE, not what they lack. "They have no document to
            edit" explains the implementation to someone who wanted to know
            whether their game still works.
          */}
          <p className="mt-1 text-xs text-gray-500">
            Playable right now, but not editable. Importing copies each one into
            the database so you can draw on it — players see no change, and it
            stays switched on.
          </p>

          <ul className="mt-2 space-y-1">
            {fileOnly.map((w) => (
              <li
                key={w.name}
                className="flex items-center gap-2 rounded bg-gray-100 px-2 py-1 text-xs dark:bg-gray-800"
              >
                <span>{w.name}</span>
                <button
                  type="button"
                  disabled={busy === w.name || busy === '*'}
                  onClick={() => onImport(w.name)}
                  className="ml-auto rounded bg-gray-200 px-2 py-0.5 text-blue-600 hover:bg-gray-300 disabled:opacity-40 dark:bg-gray-700 dark:text-blue-400 dark:hover:bg-gray-600"
                >
                  {busy === w.name ? '…' : 'Import'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

WorldList.propTypes = {
  worlds: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    enabled: PropTypes.bool,
    problem: PropTypes.string,
    hasDraft: PropTypes.bool,
    warnings: PropTypes.arrayOf(PropTypes.string),
    players: PropTypes.number,
  })),
  fileOnly: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string })),
  /** Name of the world currently being written, `'*'` for a batch, or null. */
  busy: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  /** Called with a world name, or null to import every file-only world. */
  onImport: PropTypes.func.isRequired,
};

WorldList.defaultProps = { worlds: [], fileOnly: [], busy: null };

export default WorldList;
